import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import { supabase } from '../lib/supabase'
import { useAuth, estAdmin, peutEcrire } from '../lib/auth'
import { base } from '../lib/db'
import {
  chargerFeuillets,
  detecterFeuillets,
  enregistrerFeuillets,
  ouvrirPlan
} from '../lib/plans'
import { decouperEtTeleverser, ouvrirPage } from '../lib/decoupage'
import type { DocumentChantier, Feuillet } from '../lib/types'
import { VisualiseurPdf, type SourcePages } from './VisualiseurPdf'

/**
 * Un plan ouvert : d'abord l'index des pages (code + titre de chaque
 * feuillet, avec recherche), puis le visualiseur à la page choisie.
 *
 * Deux modes :
 *  - plan découpé (le cas normal) : rien n'est téléchargé à l'ouverture ;
 *    chaque page est téléchargée seulement quand on l'ouvre (quelques
 *    centaines de Ko), puis gardée sur l'appareil ;
 *  - plan pas encore découpé : le PDF complet est téléchargé. Un
 *    administrateur sur ordinateur le découpe alors automatiquement.
 */
export function PlanOuvert({
  document: doc,
  fermer,
  statutChantier
}: {
  document: DocumentChantier
  fermer: () => void
  statutChantier?: string
}) {
  const { profil } = useAuth()
  const admin = estAdmin(profil)
  const [decoupe, setDecoupe] = useState(Boolean(doc.pages_decoupees && doc.nb_pages))
  const [total, setTotal] = useState<number | null>(decoupe ? doc.nb_pages : null)
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null) // PDF complet, si chargé
  const [telechargement, setTelechargement] = useState<number | null>(null)
  const [feuillets, setFeuillets] = useState<Feuillet[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  const [page, setPage] = useState<number | null>(null) // null = index affiché
  const [detection, setDetection] = useState<number | null>(null) // 0 à 1
  const [decoupage, setDecoupage] = useState<number | null>(null) // 0 à 1
  const arret = useRef(false)
  const pdfRef = useRef<PDFDocumentProxy | null>(null)

  // Pages d'un plan découpé déjà ouvertes : on en garde quelques-unes en
  // mémoire pour que les allers-retours soient instantanés.
  const memoire = useRef(new Map<number, Promise<PDFDocumentProxy>>())
  const sourceDecoupee = useMemo<SourcePages | null>(() => {
    if (!decoupe || !total) return null
    return {
      total,
      page: async (n) => {
        const m = memoire.current
        let p = m.get(n)
        if (!p) {
          p = ouvrirPage(doc, n)
          p.catch(() => m.delete(n))
          m.set(n, p)
          while (m.size > 6) {
            const [plusVieille] = m.keys()
            void m.get(plusVieille)?.then((d) => d.destroy()).catch(() => {})
            m.delete(plusVieille)
          }
        }
        return (await p).getPage(1)
      }
    }
  }, [decoupe, total, doc])

  const sourceComplete = useMemo<SourcePages | null>(
    () => (pdf ? { total: pdf.numPages, page: (n) => pdf.getPage(n) } : null),
    [pdf]
  )
  const source = sourceDecoupee ?? sourceComplete

  /** Charge le PDF complet (nécessaire pour découper et lire les cartouches). */
  const chargerComplet = useCallback(async (): Promise<PDFDocumentProxy> => {
    if (pdfRef.current) return pdfRef.current
    const p = await ouvrirPlan(doc, (x) => setTelechargement(x))
    setTelechargement(null)
    pdfRef.current = p
    setPdf(p)
    setTotal(p.numPages)
    if (admin && doc.nb_pages !== p.numPages) {
      await supabase.from('documents').update({ nb_pages: p.numPages }).eq('id', doc.id)
    }
    return p
  }, [doc, admin])

  async function lancerDecoupage(p: PDFDocumentProxy) {
    setDecoupage(0)
    try {
      const octets = (await base.plans.get(doc.fichier_path))?.octets ?? (await p.getData()).buffer
      await decouperEtTeleverser(
        doc,
        octets as ArrayBuffer,
        (fait, tot) => setDecoupage(fait / tot),
        () => arret.current
      )
      if (!arret.current) setDecoupe(true)
    } catch (e) {
      if (!arret.current) {
        setErreur(
          "Le plan n'a pas pu être préparé pour les téléphones : " +
            (e instanceof Error ? e.message : String(e)) +
            ' — il reste consultable en entier.'
        )
      }
    } finally {
      setDecoupage(null)
    }
  }

  async function lancerDetection(existants: Feuillet[]) {
    setDetection(0)
    setErreur(null)
    try {
      const p = await chargerComplet()
      const res = await detecterFeuillets(
        p,
        doc.id,
        existants,
        (fait, tot) => setDetection(fait / tot),
        () => arret.current
      )
      if (!arret.current) setFeuillets(res)
    } catch (e) {
      setErreur('La lecture des titres a échoué : ' + (e instanceof Error ? e.message : String(e)))
    } finally {
      setDetection(null)
    }
  }

  // Ouverture : l'index, puis (plan pas encore découpé) le PDF complet.
  useEffect(() => {
    arret.current = false
    ;(async () => {
      try {
        const f = await chargerFeuillets(doc.id).catch(() => [] as Feuillet[])
        if (arret.current) return
        setFeuillets(f)
        if (!decoupe) {
          const p = await chargerComplet()
          if (arret.current) return
          // Un administrateur prépare le plan pour les téléphones, puis lit
          // les cartouches s'il ne l'a jamais fait.
          if (admin) await lancerDecoupage(p)
          if (admin && f.length === 0 && !arret.current) await lancerDetection(f)
        } else if (admin && f.length === 0) {
          await lancerDetection(f)
        }
      } catch (e) {
        if (!arret.current) setErreur(e instanceof Error ? e.message : String(e))
      }
    })()
    const m = memoire.current
    return () => {
      arret.current = true
      for (const d of m.values()) void d.then((x) => x.destroy()).catch(() => {})
      m.clear()
      void pdfRef.current?.destroy()
      pdfRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id, doc.fichier_path])

  const parPage = useMemo(() => new Map(feuillets.map((f) => [f.page, f])), [feuillets])
  const allerA = useCallback((n: number) => setPage(n), [])
  const retourIndex = useCallback(() => setPage(null), [])

  if (source && page !== null) {
    return (
      <VisualiseurPdf
        source={source}
        nomDocument={doc.nom}
        feuillets={parPage}
        page={page}
        allerA={allerA}
        index={retourIndex}
        documentId={doc.id}
        numeroRevision={doc.numero_revision}
        peutAnnoter={peutEcrire(profil, statutChantier)}
        utilisateurId={profil?.id ?? null}
        admin={admin}
      />
    )
  }

  return (
    <div className="feuille">
      <header className="entete">
        <div className="entete-rang">
          <button className="retour" onClick={fermer}>
            ‹ Plans
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {doc.nom}
            </h1>
            <p className="sous">{total ? `${total} pages` : 'Ouverture…'}</p>
          </div>
          <button className="fermer-x" onClick={fermer} aria-label="Fermer le plan" title="Fermer">
            ✕
          </button>
        </div>
      </header>

      <div className="corps" style={{ paddingBottom: 24 }}>
        {erreur && <p className="avis erreur" style={{ marginBottom: 12 }}>{erreur}</p>}

        {decoupage !== null && (
          <div className="avis" style={{ marginBottom: 12 }}>
            <strong>Préparation pour les téléphones… {Math.round(decoupage * 100)} %</strong>
            <br />
            Chaque page est enregistrée séparément : sur le chantier, seules les pages ouvertes
            seront téléchargées. Garde cette fenêtre ouverte jusqu'à la fin.
          </div>
        )}

        {telechargement !== null && (
          <p className="vide">
            Téléchargement du plan complet… {Math.round(telechargement * 100)} %
          </p>
        )}

        {!source && !erreur && telechargement === null && <p className="vide">Ouverture du plan…</p>}

        {source && total && (
          <IndexPages
            total={total}
            parPage={parPage}
            admin={admin}
            detection={detection}
            ouvrir={setPage}
            relancer={() => void lancerDetection(feuillets)}
            arreter={() => (arret.current = true)}
            corriger={async (f) => {
              setFeuillets((liste) => [...liste.filter((x) => x.page !== f.page), f])
              try {
                await enregistrerFeuillets([f])
              } catch (e) {
                setErreur("La correction n'a pas été enregistrée : " + (e instanceof Error ? e.message : String(e)))
              }
            }}
            documentId={doc.id}
          />
        )}
      </div>
    </div>
  )
}

function IndexPages({
  total,
  parPage,
  admin,
  detection,
  ouvrir,
  relancer,
  arreter,
  corriger,
  documentId
}: {
  total: number
  parPage: Map<number, Feuillet>
  admin: boolean
  detection: number | null
  ouvrir: (page: number) => void
  relancer: () => void
  arreter: () => void
  corriger: (f: Feuillet) => void
  documentId: string
}) {
  const [recherche, setRecherche] = useState('')
  const [edition, setEdition] = useState(false)

  const pages = useMemo(() => {
    const q = recherche.trim().toLowerCase()
    const toutes = Array.from({ length: total }, (_, i) => i + 1)
    if (!q) return toutes
    return toutes.filter((n) => {
      const f = parPage.get(n)
      return (
        String(n) === q ||
        (f?.code ?? '').toLowerCase().includes(q) ||
        (f?.titre ?? '').toLowerCase().includes(q)
      )
    })
  }, [recherche, total, parPage])

  const nommees = [...parPage.values()].filter((f) => f.code || f.titre).length

  return (
    <>
      {detection !== null && (
        <div className="avis" style={{ marginBottom: 12 }}>
          <strong>Lecture des cartouches… {Math.round(detection * 100)} %</strong>
          <br />
          Chaque page est lue pour y trouver son numéro et son titre. Quand la cartouche est
          dessinée plutôt qu'écrite en texte, elle est lue par reconnaissance de texte : compte
          5 à 10 secondes par page. Tu peux déjà ouvrir les pages ; l'index se complète à la fin.{' '}
          <button className="retour" style={{ textDecoration: 'underline' }} onClick={arreter}>
            Arrêter
          </button>
        </div>
      )}

      <label className="champ" style={{ marginBottom: 8 }}>
        <input
          type="search"
          placeholder="Chercher : A304, élévation, composition, 50…"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
        />
      </label>

      <div className="sect">
        <span>
          {recherche ? `${pages.length} résultat${pages.length > 1 ? 's' : ''}` : `${total} pages`}
          {nommees > 0 && nommees < total && !recherche ? ` · ${nommees} identifiées` : ''}
        </span>
        {admin && detection === null && (
          <span style={{ display: 'flex', gap: 14 }}>
            <button className="retour" onClick={() => setEdition((x) => !x)}>
              {edition ? 'Terminer' : 'Renommer'}
            </button>
            <button className="retour" onClick={relancer} title="Les pages corrigées à la main ne sont pas modifiées">
              Relire les cartouches
            </button>
          </span>
        )}
      </div>

      <div className="liste" style={{ gap: 4 }}>
        {pages.map((n) => {
          const f = parPage.get(n)
          if (edition) {
            return (
              <LigneEdition
                key={n}
                page={n}
                f={f}
                enregistrer={(code, titre) =>
                  corriger({
                    document_id: documentId,
                    page: n,
                    code: code || null,
                    titre: titre || null,
                    source: 'manuel'
                  })
                }
              />
            )
          }
          return (
            <button
              key={n}
              className="carte bande-nulle"
              style={{ padding: '9px 12px', display: 'flex', gap: 12, alignItems: 'baseline' }}
              onClick={() => ouvrir(n)}
            >
              <span
                style={{
                  fontFamily: 'var(--mono)',
                  fontWeight: 800,
                  fontSize: 13,
                  minWidth: 64,
                  color: f?.code ? 'var(--ink)' : 'var(--ink3)'
                }}
              >
                {f?.code ?? '—'}
              </span>
              <span className="titre" style={{ flex: 1, textTransform: 'uppercase' }}>
                {f?.titre ?? <span style={{ color: 'var(--ink3)', fontWeight: 400 }}>Sans titre</span>}
              </span>
              <span className="code">p. {n}</span>
            </button>
          )
        })}
      </div>
    </>
  )
}

function LigneEdition({
  page,
  f,
  enregistrer
}: {
  page: number
  f: Feuillet | undefined
  enregistrer: (code: string, titre: string) => void
}) {
  const [code, setCode] = useState(f?.code ?? '')
  const [titre, setTitre] = useState(f?.titre ?? '')
  const modifie = code !== (f?.code ?? '') || titre !== (f?.titre ?? '')
  const sauver = () => modifie && enregistrer(code.trim().toUpperCase(), titre.trim())

  return (
    <div className="carte bande-nulle" style={{ padding: '7px 10px', display: 'flex', gap: 8, alignItems: 'center' }}>
      <input
        value={code}
        onChange={(e) => setCode(e.target.value)}
        onBlur={sauver}
        placeholder="Code"
        aria-label={`Code de la page ${page}`}
        style={{ width: 80, fontFamily: 'var(--mono)', border: '1.5px solid var(--line-strong)', padding: '6px 7px', background: 'var(--ground)' }}
      />
      <input
        value={titre}
        onChange={(e) => setTitre(e.target.value)}
        onBlur={sauver}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        placeholder="Titre de la page"
        aria-label={`Titre de la page ${page}`}
        style={{ flex: 1, minWidth: 0, border: '1.5px solid var(--line-strong)', padding: '6px 7px', background: 'var(--ground)' }}
      />
      <span className="code" style={{ minWidth: 42, textAlign: 'right' }}>
        p. {page}
      </span>
    </div>
  )
}
